import { Box, Button, Stack, styled, Typography, useTheme } from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useMemo } from 'react';

import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { BlockContainer, BlockContent } from 'src/components/Layout/named-layout-block.view';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import { CustomViewEntity, useCustomViews } from 'src/graphql/entities/custom-view';
import { useGlobalStore } from 'src/store';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { MAP_ENTITY_TO_URL_ENTITY, MAP_URL_ENTITY_NAME_TO_ENTITY } from './constants';
import { CONVERSATIONS_VIEW_WRAPPER_STYLES } from './custom-views-selected-view/constants';
import { getFirstAccessibleTabPath, useCustomViewTabs } from './hooks/use-view-tabs';

const StyledTab = styled(Button)(({ theme }) => ({
  padding: theme.spacing(0.2, 1),
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  background: 'transparent',
  border: `1px solid ${theme.palette.custom.OV.sys.onSurfaceVariant}`,

  '&.Mui-disabled': {
    background: theme.palette.custom.OV.sys.surfaceContainerHighest,
    border: `1px solid ${theme.palette.custom.OV.sys.surfaceContainerHighest}`,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  },
}));

export const CustomViews = () => {
  const { pathname } = useLocation();

  const { entityName } = useRouteParams(UrlParams.entityName);

  const { tabs, loading } = useCustomViewTabs();

  const selectedEntity = MAP_URL_ENTITY_NAME_TO_ENTITY[entityName] ?? entityName;

  const { customViewId: selectedCustomViewId } = useRouteParams(UrlParams.customViewId);

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { navigate, partnerPrefix } = useNavigateRoute();
  const theme = useTheme();

  const setSelectedEntity = useCallback(
    (entity: CustomViewEntity) =>
      navigate(portalPaths.customView.view(MAP_ENTITY_TO_URL_ENTITY[entity] ?? entity)),
    [navigate],
  );

  const { customViews } = useCustomViews({
    entity: selectedEntity,
    partnerId: selectedPartnerId,
  });

  const selectedView = useMemo(
    () => customViews.find((view) => view.customViewId === selectedCustomViewId),
    [customViews, selectedCustomViewId],
  );

  const showBreadcrumbs = selectedView?.name && !pathname.includes('update');

  const hasAccessToEntity = useMemo(() => {
    if (loading) {
      return true;
    }

    return tabs.some((tab) => tab.entity === selectedEntity);
  }, [tabs, loading, selectedEntity]);

  if (!hasAccessToEntity) {
    return <Navigate to={getFirstAccessibleTabPath(tabs, partnerPrefix)} replace />;
  }

  return (
    <Box sx={CONVERSATIONS_VIEW_WRAPPER_STYLES}>
      <BlockContainer
        sx={{
          border: showBreadcrumbs ? theme.utils.important('none') : undefined,
        }}
      >
        <BlockContent sx={{ padding: 0 }}>
          {!showBreadcrumbs && (
            <Stack
              flexDirection="row"
              alignItems="center"
              padding={theme.spacing(1.1, 1)}
              borderBottom={`1px solid ${theme.palette.custom.OV.sys.outlineVariant}`}
            >
              <DrawerCloseControl />
              <Typography variant="subtitle2" sx={{ marginRight: '1rem', fontWeight: 700 }}>
                Views
              </Typography>
              <Stack flexDirection="row" alignItems="center" gap="0.5rem" flexWrap="wrap">
                {tabs.map((tab) => (
                  <StyledTab
                    key={tab.entity}
                    variant="outlined"
                    disabled={tab.entity === selectedEntity}
                    onClick={() => setSelectedEntity(tab.entity)}
                  >
                    {tab.label}
                  </StyledTab>
                ))}
              </Stack>
            </Stack>
          )}
          <Outlet />
        </BlockContent>
      </BlockContainer>
    </Box>
  );
};

export default CustomViews;
